import { animated, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import { Navigate, useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';

import type { InvalidNickname } from '@sorare/wallet-shared';

import { Sport } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { StarBall } from '#src/atoms/icons/StarBall/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconArrowLeft } from '#src/atoms/icons/system/iconArrowLeft.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { LabelL } from '#src/atoms/typography/index.tsx';
import { useDefaultSportPages } from '#src/constants/routes.ts';
import type { SignUpConfig } from '#src/contexts/connection/index.tsx';
import { useConnectionContext } from '#src/contexts/connection/index.tsx';
import { useIntlContext } from '#src/contexts/intl/index.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useMessagingContext } from '#src/contexts/wallet/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useEvents } from '#src/lib/events/useEvents.ts';
import { glossary } from '#src/lib/glossary.ts';
import { absCenter } from '#src/lib/style.ts';
import { desktopAndAbove, laptopAndAbove } from '#src/style/mediaQuery.ts';

import { SportContainer } from './SportContainer.tsx';
import nbaBg from './assets/DonovanMitchel.webp';
import mlbBg from './assets/JulioRodriguez.webp';
import footballBg from './assets/football.webp';

const Wrapper = styled.div`
  flex: 1;
  height: 100%;
  display: grid;
  overflow: hidden;
  position: relative;
  grid-template-rows: 1fr 1fr 1fr;
  transition: 500ms;

  @media ${laptopAndAbove} {
    &:not(.step2) {
      aspect-ratio: 1440 / 810;
    }
    &.newFootballDesign {
      aspect-ratio: unset;
      min-height: 100vh;
    }
  }

  @media ${desktopAndAbove} {
    max-height: 100vh;
    aspect-ratio: 1440 / 810;
    min-height: min(780px, 100vh);
    &.newFootballDesign {
      min-height: 100vh;
    }
  }

  &:not(.step0) {
    &.FOOTBALL {
      grid-template-rows: 3fr 0fr 0fr;
    }
    &.NBA {
      grid-template-rows: 0fr 3fr 0fr;
    }
    &.BASEBALL {
      grid-template-rows: 0fr 0fr 3fr;
    }
  }

  @media ${laptopAndAbove} {
    grid-auto-flow: column;
    grid-template-rows: none;
    grid-template-columns: 1fr 1fr 1fr;

    &:not(.step0) {
      &.FOOTBALL {
        grid-template-rows: none;
        grid-template-columns: 3fr 0fr 0fr;
      }
      &.NBA {
        grid-template-rows: none;
        grid-template-columns: 0fr 3fr 0fr;
      }
      &.BASEBALL {
        grid-template-rows: none;
        grid-template-columns: 0fr 0fr 3fr;
      }
    }
  }
`;

const CloseButtonWrapper = styled.div`
  z-index: 10;
  position: absolute;
  top: var(--double-unit);
  padding: 0 var(--double-unit);
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

const Title = styled(animated.h1)`
  ${absCenter}
  z-index: 1;
  display: flex;
  font-weight: 400;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  letter-spacing: -1px;
  width: 100%;
  padding: 0 var(--unit);
  @media ${laptopAndAbove} {
    font-size: 48px;
  }
`;

const SmallStarBall = styled(StarBall)`
  height: 26px;
`;

const messages = defineMessages({
  title: {
    defaultMessage: 'Put your manager skills to the test',
  },
  football: {
    defaultMessage: 'Start with Football',
  },
  footballSecondaryStep: {
    defaultMessage: 'Time to build your Club!',
  },
  footballSecondaryStepSub: {
    defaultMessage: 'Choose your Manager name',
  },
  nba: {
    defaultMessage: 'Start with Basketball',
  },
  nbaSecondaryStep: {
    defaultMessage: 'Choose your NBA Manager name',
  },
  nbaSecondaryStepSub: {
    defaultMessage: 'Pick a name the world of basketball will fear.',
  },
  baseball: {
    defaultMessage: 'Start with Baseball',
  },
  mlbSecondaryStep: {
    defaultMessage: 'Choose your MLB Manager name',
  },
  mlbSecondaryStepSub: {
    defaultMessage: 'Pick a name the world of baseball will fear.',
  },
});

const useActiveSport = (skipSportSelection?: boolean) => {
  const { sport: sportFromContext } = useSportContext();
  const location = useLocation();
  const defaultSport = skipSportSelection ? sportFromContext : undefined;
  const [state, setState] = useState<Sport | undefined>(defaultSport);
  const sportLanding = useDefaultSportPages(true);
  return {
    activeSport: state,
    setActiveSport: setState,
    getAfterLoggedInTarget: (sport: Sport) => ({
      afterLoggedInTarget: sportLanding[sport],
      ...location.state,
    }),
  };
};

type Props = {
  onClose: () => void;
  WalletPortal: ReactNode;
  skipSportSelection?: boolean;
  signupConfig?: SignUpConfig | null;
};

export const ChooseYourSport = ({
  onClose,
  WalletPortal,
  skipSportSelection,
  signupConfig,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const location = useLocation();

  const [step, setStep] = useState<0 | 1 | 2>(0);
  const { activeSport, setActiveSport, getAfterLoggedInTarget } =
    useActiveSport(skipSportSelection);
  const { signIn } = useConnectionContext();
  const { registerHandler } = useMessagingContext();
  const track = useEvents();
  const navigate = useNavigate();
  const [usernameError, setUsernameError] = useState<string | undefined>();

  const handleClose = () => {
    if (skipSportSelection) {
      onClose();
      return;
    }
    switch (step) {
      case 1:
        setStep(0);
        setActiveSport(undefined);
        break;
      case 2:
        setStep(1);
        setUsernameError(undefined);
        break;
      default:
        onClose();
    }
  };

  useEffect(() => {
    registerHandler<InvalidNickname>('invalidNickname', async ({ error }) => {
      setUsernameError(error);
      setStep(0);
      return {};
    });
  }, [registerHandler]);

  const transitions = useTransition(step === 0, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { duration: 300 },
  });

  const handleSportClick = (sport: Sport) => {
    setStep(1);
    setActiveSport(sport);
    // store the sport in the location state so we can redirect to the correct sport home page after signup
    navigate(location, {
      state: getAfterLoggedInTarget(sport),
      replace: true,
    });
    track('Choose Signup Sport', { sport });
  };

  const defaultProps = {
    step,
    WalletPortal,
    goToNextStep: () => setStep(2),
    nickname: signupConfig?.nickname,
    usernameError,
  };

  if (activeSport && step === 0) {
    setStep(signupConfig?.step || 1);
    return (
      <Navigate
        to={location}
        state={getAfterLoggedInTarget(activeSport)}
        replace
      />
    );
  }

  return (
    <Wrapper
      className={classNames(activeSport, `step${step}`, {
        newFootballDesign: activeSport === Sport.FOOTBALL,
      })}
    >
      <CloseButtonWrapper>
        <div>
          {!!(step > 0 || skipSportSelection) && (
            <IconButton
              size="medium"
              onClick={handleClose}
              disableDebounce
              color="transparent"
            >
              <FontAwesomeIcon
                size="2x"
                icon={step === 0 || skipSportSelection ? iconX : iconArrowLeft}
                color="white"
              />
            </IconButton>
          )}
        </div>
        <Horizontal gap={1}>
          {!!isLaptopAndAbove && (
            <LabelL as="span" color="var(--c-nd-900)">
              <FormattedMessage defaultMessage="Have an account?" />
            </LabelL>
          )}
          <Button size="small" color="secondary" onClick={() => signIn()}>
            <FormattedMessage {...glossary.signin} />
          </Button>
        </Horizontal>
      </CloseButtonWrapper>
      {!!isLaptopAndAbove &&
        transitions(
          (styles, item) =>
            item && (
              <Title style={styles}>
                {formatMessage(messages.title)}
                <SmallStarBall />
              </Title>
            )
        )}
      <SportContainer
        {...defaultProps}
        sport={Sport.FOOTBALL}
        background={footballBg}
        active={activeSport === Sport.FOOTBALL}
        onClick={() => handleSportClick(Sport.FOOTBALL)}
        startButtonText={formatMessage(messages.football)}
        secondaryStepTitle={formatMessage(messages.footballSecondaryStep)}
        secondaryStepSubtitle={formatMessage(messages.footballSecondaryStepSub)}
      />
      <SportContainer
        {...defaultProps}
        sport={Sport.NBA}
        background={nbaBg}
        active={activeSport === Sport.NBA}
        onClick={() => handleSportClick(Sport.NBA)}
        startButtonText={formatMessage(messages.nba)}
        secondaryStepTitle={formatMessage(messages.nbaSecondaryStep)}
        secondaryStepSubtitle={formatMessage(messages.nbaSecondaryStepSub)}
      />
      <SportContainer
        {...defaultProps}
        sport={Sport.BASEBALL}
        background={mlbBg}
        active={activeSport === Sport.BASEBALL}
        onClick={() => handleSportClick(Sport.BASEBALL)}
        startButtonText={formatMessage(messages.baseball)}
        secondaryStepTitle={formatMessage(messages.mlbSecondaryStep)}
        secondaryStepSubtitle={formatMessage(messages.mlbSecondaryStepSub)}
      />
    </Wrapper>
  );
};
